/* eslint-disable */
import { useEffect, useRef, useState } from 'react';
import { Bot, Check, Copy, User } from 'lucide-react';
import PropTypes from 'prop-types';
import LoadingIndicator from './LoadingIndicator';
import {
  formatDataSourceLabel,
  getAssistantBubbleClasses,
  getUserBubbleClasses,
} from '../../utils/ai/chatTheme';

const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(timestamp));
  } catch (error) {
    return '';
  }
};

const ChatMessageList = (props) => {
  const { messages = [], theme = 'dark', loading = false } = props;
  const endRef = useRef(null);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, loading]);

  const handleCopy = async (message) => {
    try {
      await navigator.clipboard.writeText(message.content || '');
      setCopiedId(message.id);
      window.setTimeout(() => setCopiedId(null), 1400);
    } catch (error) {
      setCopiedId(null);
    }
  };

  return (
    <div
      className="flex-1 overflow-y-auto px-4 py-4 overscroll-contain"
      style={{ overscrollBehavior: 'contain' }}
    >
      <div className="flex flex-col gap-4">
        {messages.map((message) => {
          const isUser = message.role === 'user';
          const attachment = message.attachment;
          const hasAttachment = Boolean(attachment?.previewUrl || attachment?.name);
          const hasContent = Boolean(String(message.content || '').trim());
          const dataSources = Array.isArray(message.dataSources) ? message.dataSources : [];

          return (
            <div key={message.id} className={`flex gap-2.5 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
              <span
                className={`mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] ${
                  isUser
                    ? theme === 'dark'
                      ? 'bg-[#25384F] text-[#C8C8C8]'
                      : 'bg-[#EFF8FE] text-[#066FD1]'
                    : 'bg-[#066FD1] text-white'
                }`}
                aria-hidden="true"
              >
                {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
              </span>

              <div className={`max-w-[82%] ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
                <div
                  className={`rounded-[10px] px-3.5 py-2.5 text-sm shadow-sm ${
                    isUser ? getUserBubbleClasses(theme) : getAssistantBubbleClasses(theme)
                  }`}
                >
                  <>
                    {hasAttachment ? (
                      <div
                        className={`mb-3 overflow-hidden rounded-[10px] border ${
                          theme === 'dark' ? 'border-[#25384F]' : 'border-[#D1E2F0]'
                        }`}
                      >
                        {attachment?.previewUrl ? (
                          <img
                            src={attachment.previewUrl}
                            alt={attachment?.name ? `${attachment.name} preview` : 'Attached screenshot preview'}
                            className="max-h-44 w-full object-cover"
                          />
                        ) : (
                          <div className="flex min-h-28 items-center justify-center px-4 py-6 text-xs font-semibold uppercase tracking-[0.18em] opacity-80">
                            Screenshot attached
                          </div>
                        )}
                      </div>
                    ) : null}
                    {hasContent ? <p className="whitespace-pre-wrap leading-6">{message.content}</p> : null}
                    <div className="mt-2 flex items-center justify-between gap-3">
                      {message.timestamp ? (
                        <p
                          className={`text-[11px] ${
                            isUser
                              ? 'text-white/75'
                              : theme === 'dark'
                                ? 'text-[#8CA0B8]'
                                : 'text-[#6C7A91]'
                          }`}
                        >
                          {formatTimestamp(message.timestamp)}
                        </p>
                      ) : (
                        <span />
                      )}
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleCopy(message)}
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-md transition ${
                            isUser
                              ? 'text-white/75 hover:bg-white/15 hover:text-white'
                              : theme === 'dark'
                                ? 'text-[#8CA0B8] hover:bg-[#25384F] hover:text-white'
                                : 'text-[#6C7A91] hover:bg-[#EFF8FE] hover:text-[#066FD1]'
                          }`}
                          aria-label={`Copy ${isUser ? 'user' : 'assistant'} message`}
                        >
                          {copiedId === message.id ? (
                            <Check className="h-3.5 w-3.5 text-[#1C8C4A]" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                </div>

                {!isUser && dataSources.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {dataSources.map((source) => (
                      <span
                        key={`${message.id}-${source}`}
                        className={`rounded-md px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                          theme === 'dark'
                            ? 'bg-[#25384F] text-[#8CA0B8]'
                            : 'bg-[#EFF8FE] text-[#24527A]'
                        }`}
                      >
                        {formatDataSourceLabel(source)}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}

        {loading ? (
          <div className="flex gap-2.5">
            <span className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#066FD1] text-white">
              <Bot className="h-4 w-4" />
            </span>
            <LoadingIndicator label="Analyzing metrics..." theme={theme} />
          </div>
        ) : null}

        <div ref={endRef} />
      </div>
    </div>
  );
};

ChatMessageList.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      role: PropTypes.oneOf(['user', 'assistant']),
      content: PropTypes.string,
      timestamp: PropTypes.string,
      intent: PropTypes.string,
      dataSources: PropTypes.arrayOf(PropTypes.string),
      attachment: PropTypes.shape({
        name: PropTypes.string,
        size: PropTypes.number,
        type: PropTypes.string,
        previewUrl: PropTypes.string,
      }),
      requestMessage: PropTypes.string,
    }),
  ),
  theme: PropTypes.oneOf(['light', 'dark']),
  loading: PropTypes.bool,
};

export default ChatMessageList;